import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// third-party
import { useLanguage } from '../../../../../utils/i18n';

// project imports
import NavItem from './NavItem';
import NavCollapse from './NavCollapse';
import SimpleBar from 'components/third-party/SimpleBar';
import Transitions from 'components/@extended/Transitions';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import useHeaderTheme from 'hooks/useHeaderTheme';
import { useGetMenuMaster } from 'api/menu';

// assets
import DownOutlined from '@ant-design/icons/DownOutlined';
import GroupOutlined from '@ant-design/icons/GroupOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';

const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 180,
  '&:before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    top: 5,
    left: 32,
    width: 12,
    height: 12,
    transform: 'translateY(-50%) rotate(45deg)',
    zIndex: 120,
    borderWidth: '6px',
    borderStyle: 'solid',
    borderColor: `${theme.palette.background.paper}  transparent transparent ${theme.palette.background.paper}`
  }
}));

// --- helpers ---
export const getParentPathForHiddenRoute = (path) => {
  const p = String(path || '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
  if (p === 'history-download-video' || p === 'history-video') return 'live-streaming';
  if (p === 'dashboard' || p === 'dashboard') return 'dashboard-vehicle';
  return p;
};

function stripQueryAndSlashes(u = '') {
  if (!u) return '';
  const withoutQuery = String(u).split('?')[0];
  return withoutQuery.replace(/^\/+|\/+$/g, '');
}

function matchesPath(targetPath = '', candidate = '') {
  const t = getParentPathForHiddenRoute(targetPath);
  const c = String(candidate || '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();

  if (!c) return false;
  if (t === c) return true;
  if (t.startsWith(c + '/')) return true;
  if (c.includes(':')) {
    const pattern = '^' + c.replace(/:[^/]+/g, '[^/]+') + '$';
    try {
      return new RegExp(pattern, 'i').test(t);
    } catch (e) {
      console.log(e);
      return false;
    }
  }
  if (t.endsWith('/' + c) || c.endsWith('/' + t)) return true;
  const tLast = t.split('/').filter(Boolean).pop() || '';
  const cLast = c.split('/').filter(Boolean).pop() || '';
  if (tLast && cLast && tLast === cLast) return true;
  return false;
}

function findMatchRecursively(nodes = [], targetPath = '', nameFromQuery = '') {
  if (!Array.isArray(nodes)) return null;
  for (const node of nodes) {
    const rawUrl = node?.url || node?.link || '';
    const nodeUrl = stripQueryAndSlashes(rawUrl);
    if (nodeUrl && matchesPath(targetPath, nodeUrl)) return { node, path: nodeUrl };

    if (Array.isArray(node.children) && node.children.length) {
      const found = findMatchRecursively(node.children, targetPath, nameFromQuery);
      if (found) return found;
    }
  }
  return null;
}

// ==============================|| NAV GROUP ||============================== //

export default function NavGroup({
  item,
  lastItem,
  remItems,
  lastItemId,
  selectedID,
  setSelectedID,
  setSelectedItems,
  selectedItems,
  setSelectedLevel,
  selectedLevel
}) {
  const { pathname, search } = useLocation();
  const { menuOrientation } = useConfig();
  const { headerColor, iconColor } = useHeaderTheme();
  const { t } = useLanguage();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentItem, setCurrentItem] = useState(item);

  const openMini = Boolean(anchorEl);
  const qs = new URLSearchParams(search);
  const rawNameFromQuery = (qs.get('name') || '').toLowerCase();
  const nameFromQuery = getParentPathForHiddenRoute(rawNameFromQuery);
  const targetPath = getParentPathForHiddenRoute(pathname);

  useEffect(() => {
    if (lastItem && item.id === lastItemId) {
      const localItem = { ...item };
      const elements = Array.isArray(remItems) ? remItems.map((ele) => ele.elements) : [];
      localItem.children = elements.flat(1);
      setCurrentItem(localItem);
    } else {
      setCurrentItem(item);
    }
  }, [item, lastItem, lastItemId, remItems]);

  useEffect(() => {
    if (!currentItem) return;
    const rawUrl = currentItem?.url || currentItem?.link || '';
    const groupUrl = stripQueryAndSlashes(rawUrl);
    let matched = false;
    if (groupUrl && matchesPath(targetPath, groupUrl)) {
      matched = true;
    } else {
      const children = Array.isArray(currentItem.children) ? currentItem.children : [];
      const found = findMatchRecursively(children, targetPath, nameFromQuery);
      if (found) matched = true;
    }

    if (matched) {
      setSelectedID(currentItem.id);
      setSelectedItems(currentItem.id);
      setSelectedLevel(1);
      setAnchorEl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, nameFromQuery, currentItem]);

  const handleClick = (event) => {
    if (!openMini) {
      setAnchorEl(event?.currentTarget);
    } else {
      setAnchorEl(null);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isSelected = selectedID === currentItem.id;
  const Icon = currentItem?.icon ? currentItem.icon : null;
  const itemIcon = Icon ? <Icon style={{ fontSize: 20, stroke: '1.5' }} /> : null;

  const navCollapse = currentItem.children?.map((menuItem, index) => {
    switch (menuItem.type) {
      case 'collapse':
        return (
          <NavCollapse
            key={menuItem.id}
            menu={menuItem}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
            level={1}
            parentId={currentItem.id}
          />
        );
      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={1} />;
      default:
        return (
          <Typography key={index} variant="h6" color="error" align="center">
            Fix - Group Collapse or Items
          </Typography>
        );
    }
  });

  const moreItems = Array.isArray(remItems)
    ? remItems.map((itemRem, i) => (
        <Fragment key={i}>
          {itemRem.url ? (
            <NavItem item={itemRem} level={1} />
          ) : itemRem.title ? (
            <Typography variant="caption" sx={{ pl: 2, color: 'text.secondary' }}>
              {itemRem.title}
            </Typography>
          ) : null}
          {itemRem?.elements?.map((menu) => {
            switch (menu.type) {
              case 'collapse':
                return (
                  <NavCollapse
                    key={menu.id}
                    menu={menu}
                    level={1}
                    parentId={currentItem.id}
                    setSelectedItems={setSelectedItems}
                    setSelectedLevel={setSelectedLevel}
                    selectedLevel={selectedLevel}
                    selectedItems={selectedItems}
                  />
                );
              case 'item':
                return <NavItem key={menu.id} item={menu} level={1} />;
              default:
                return (
                  <Typography key={menu.id} variant="h6" color="error" align="center">
                    Menu Items Error
                  </Typography>
                );
            }
          })}
        </Fragment>
      ))
    : null;

  const items = currentItem.children?.map((menu) => {
    switch (menu?.type) {
      case 'collapse':
        return (
          <NavCollapse
            key={menu.id}
            menu={menu}
            level={1}
            parentId={currentItem.id}
            setSelectedItems={setSelectedItems}
            setSelectedLevel={setSelectedLevel}
            selectedLevel={selectedLevel}
            selectedItems={selectedItems}
          />
        );
      case 'item':
        return <NavItem key={menu.id} item={menu} level={1} />;
      default:
        return (
          <Typography key={menu?.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  });

  const popperId = openMini ? `group-pop-${item.id}` : undefined;

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <List
          subheader={
            <>
              {item.title ? (
                drawerOpen && (
                  <Box sx={{ pl: 3, mb: 1.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: 'text.primary', // Menggunakan warna gelap agar terlihat di drawer putih
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {t(item.title)}
                    </Typography>
                    {item.caption && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t(item.caption)}
                      </Typography>
                    )}
                  </Box>
                )
              ) : (
                <Divider sx={{ my: 0.5, display: 'none' }} />
              )}
            </>
          }
          sx={{ mt: drawerOpen && item.title ? 1.5 : 0, py: 0, zIndex: 0 }}
        >
          {navCollapse}
        </List>
      ) : (
        <List>
          <ListItemButton
            selected={isSelected}
            sx={{ p: 1, my: 0.5, mr: 1, display: 'flex', alignItems: 'center', '&.Mui-selected': { bgcolor: 'transparent' } }}
            onMouseEnter={handleClick}
            onClick={handleClick}
            onMouseLeave={handleClose}
            aria-describedby={popperId}
            className={anchorEl ? 'Mui-selected' : ''}
          >
            {(itemIcon || currentItem.id === lastItemId) && (
              <ListItemIcon sx={{ minWidth: 28, color: isSelected ? iconColor : 'text.primary' }}>
                {currentItem.id === lastItemId ? <GroupOutlined style={{ fontSize: 20, stroke: '1.5' }} /> : itemIcon}
              </ListItemIcon>
            )}
            <ListItemText
              sx={{ mr: 1 }}
              primary={
                <Typography variant="body1" sx={{ color: isSelected ? iconColor : 'text.primary', fontWeight: 600 }}>
                  {t(currentItem.id === lastItemId ? 'more-items' : currentItem.title)}
                </Typography>
              }
            />
            {openMini ? (
              <DownOutlined style={{ fontSize: 16, stroke: '1.5', color: headerColor || '#485D9A' }} />
            ) : (
              <RightOutlined style={{ fontSize: 16, stroke: '1.5', color: headerColor || '#485D9A' }} />
            )}
            {anchorEl && (
              <PopperStyled id={popperId} open={openMini} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 2001 }}>
                {({ TransitionProps }) => (
                  <Transitions in={openMini} {...TransitionProps}>
                    <Paper sx={(theme) => ({ mt: 0.5, py: 1.25, boxShadow: theme.shadows[8], backgroundImage: 'none' })}>
                      <ClickAwayListener onClickAway={handleClose}>
                        <>
                          <SimpleBar sx={{ minWidth: 200, overflowX: 'hidden', overflowY: 'auto', maxHeight: 'calc(100vh - 170px)' }}>
                            {currentItem.id !== lastItemId ? items : moreItems}
                          </SimpleBar>
                        </>
                      </ClickAwayListener>
                    </Paper>
                  </Transitions>
                )}
              </PopperStyled>
            )}
          </ListItemButton>
        </List>
      )}
    </>
  );
}

NavGroup.propTypes = {
  item: PropTypes.any,
  lastItem: PropTypes.number,
  remItems: PropTypes.array,
  lastItemId: PropTypes.string,
  selectedID: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  setSelectedID: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
  setSelectedItems: PropTypes.oneOfType([PropTypes.func, PropTypes.any]),
  selectedItems: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  setSelectedLevel: PropTypes.func,
  selectedLevel: PropTypes.number
};
